import "./menu.css";

import { ComponentProps } from "react";
import { Platform, StyleProp, TextStyle, View, ViewStyle } from "react-native";
import { color } from "src/styles";
import fontFamilies from "src/styles/fontFamilies";
import { basicFont, smallFont } from "src/styles/fonts";
import { radiusMedium } from "src/styles/radius";
import { paddingLarge, paddingSmall } from "src/styles/spacing";
import * as DropdownMenu from "zeego/dropdown-menu";
import type { MenuItemSubtitleProps } from "zeego/lib/typescript/menu";
import MenuItemContext, { useMenuItemContext } from "./contexts/MenuItemContext";

const theme = "Dark" as const;
const styles = {
	destructive: {
		color: color.Destructive[theme],
	},
} satisfies Record<string, StyleProp<ViewStyle | TextStyle>>;

const halfItemSpacerHeight = 2;

const StyledRoot = DropdownMenu.create<ComponentProps<typeof DropdownMenu.Root>>(
	(props) => <DropdownMenu.Root {...props} />,
	"Root",
);

const StyledTrigger = DropdownMenu.create<ComponentProps<typeof DropdownMenu.Trigger>>(
	({ asChild = true, children, ...props }) => (
		<DropdownMenu.Trigger {...props} asChild={asChild}>
			{/* For some reason the trigger doesn't work without this view */}
			<View>{children}</View>
		</DropdownMenu.Trigger>
	),
	"Trigger",
);

const StyledContent = DropdownMenu.create<ComponentProps<typeof DropdownMenu.Content>>(
	({ style, ...props }) => (
		<DropdownMenu.Content
			className="DropdownMenuContent"
			{...props}
			style={{
				minWidth: 220,
				margin: paddingSmall,
				borderRadius: radiusMedium,
				backgroundColor: color.MenuBackgroundOpaque[theme],
				overflow: "hidden",
				zIndex: 1000,
				...Platform.select({
					web: {
						backdropFilter: "blur(10px)",
						WebkitBackdropFilter: "blur(10px)",
						boxShadow:
							"0px 10px 38px -10px rgba(22, 23, 24, 0.35), 0px 10px 20px -15px rgba(22, 23, 24, 0.2)",
					},
				}),
				...style,
			}}
		/>
	),
	"Content",
);

const StyledItem = DropdownMenu.create<
	ComponentProps<typeof DropdownMenu.Item> & { destructive?: boolean }
>(
	({ style, destructive, disabled, ...props }) => (
		<MenuItemContext.Provider
			value={{
				destructive,
			}}
		>
			<DropdownMenu.Item
				{...props}
				className="DropdownMenuItem"
				disabled={disabled}
				style={{
					display: "flex",
					flex: 1,
					cursor: disabled ? "not-allowed" : "pointer",
					alignItems: "center",
					minHeight: 44,
					minWidth: 254,
					justifyContent: "flex-start",
					paddingLeft: paddingLarge,
					paddingRight: paddingLarge,

					// This styling is wack. If you're ever confused how it works, just set `halfItemSpacerHeight` to 20 and see that it's still centered
					borderTopWidth: halfItemSpacerHeight,
					marginTop: -halfItemSpacerHeight,
					borderBottomWidth: halfItemSpacerHeight,
					marginBottom: -halfItemSpacerHeight,
					paddingBottom: halfItemSpacerHeight,
					borderTopStyle: "solid",
					borderColor: color.MenuSpacerColor[theme],

					opacity: disabled ? 0.5 : undefined,
					...style,
				}}
			/>
		</MenuItemContext.Provider>
	),
	"Item",
);
const StyledItemTitle = DropdownMenu.create<ComponentProps<typeof DropdownMenu.ItemTitle>>(
	({ style, ...props }) => {
		const { destructive } = useMenuItemContext();

		return (
			<DropdownMenu.ItemTitle
				{...props}
				style={{
					display: "flex",
					...basicFont,
					fontFamily: fontFamilies.regular,
					...(destructive ? styles.destructive : null),
					...style,
				}}
			/>
		);
	},
	"ItemTitle",
);
const StyledItemSubtitle = DropdownMenu.create<MenuItemSubtitleProps>(({ style, ...props }) => {
	const { destructive } = useMenuItemContext();
	return (
		<DropdownMenu.ItemSubtitle
			{...props}
			style={{
				display: "flex",
				...smallFont,
				color: color.TextLight[theme],
				fontFamily: fontFamilies.regular,
				...(destructive ? styles.destructive : null),
				...style,
			}}
		/>
	);
}, "ItemSubtitle");
const StyledGroup = DropdownMenu.create<ComponentProps<typeof DropdownMenu.Group>>(
	({ style, ...props }) => <DropdownMenu.Group {...props} style={style} />,
	"Group",
);
const StyledSeparator = DropdownMenu.create<
	Omit<ComponentProps<typeof DropdownMenu.Separator>, "children">
>(
	({ style, ...props }) => (
		<DropdownMenu.Separator
			{...props}
			style={{
				width: "100%",
				borderTopStyle: "solid",
				borderTopWidth: halfItemSpacerHeight,
				borderTopColor: color.MenuSpacerColor[theme],
				height: halfItemSpacerHeight,
				...style,
			}}
		/>
	),
	"Separator",
);

const Menu = {
	Root: StyledRoot,
	Trigger: StyledTrigger,
	Content: StyledContent,
	Item: StyledItem,
	ItemTitle: StyledItemTitle,
	ItemSubtitle: StyledItemSubtitle,
	Group: StyledGroup,
	Separator: StyledSeparator,
};
export default Menu;
